import authStore from '../store/auth';
import { useUserGroupsCurrentUserAdminState } from '../services/user.service';

// Legacy hardcoded flags must be registered here (used by FEATURE_TOGGLES_DATA).
// Company-managed flags (created via admin center) work automatically via
// useFeatureFlag(key) and useCompanyFeatures() — registering them here is
// optional and only needed if you want a named constant instead of a raw string.
export const FEATURE_FLAG_NAMES: Record<string, string> = {
  TEST_GLOBAL_FLAG: 'TEST_GLOBAL_FLAG',
  TEST_GROUP_FLAG: 'TEST_GROUP_FLAG',
  TEST_EMAIL_FLAG: 'TEST_EMAIL_FLAG',
  FORM_PF: 'form_pf',
  DATOX_COWORK: 'datox_cowork',
  AI_REPORT_REVIEW: 'ai_report_review',
  FILE_UPLOAD_CLOUD_INTEGRATION: 'file_upload_cloud_integration',
  // Company-managed flag (admin center). Gates the Lookup Tables tab on
  // the Extraction page + the Lookup tab in the Join transformation drawer.
  LOOKUP_TABLE: 'lookup_table',
  // Company-managed flag (admin center). Gates the Enrich by Datox transformation action — the
  // catalogue entry and the toolbar's "fetch missing" button. A step already saved with it stays
  // readable and editable either way; the flag only decides who can add one.
  ENRICH_INSTRUMENT_DATA: 'enrich_instrument_data',
  V2_UI: 'v2_ui',
  // Company-managed flag (admin center). Gates the Work queue: its rail item and ?view=work.
  WORK_QUEUE: 'work_queue'
};

interface FeatureToggle {
  isEnabledGlobally: boolean;
  enabledUserGroups: string[];
  enabledUserEmails: string[];
}

type AuthStore = Record<string, any>;

export const FEATURE_TOGGLES_DATA: Record<string, FeatureToggle> = {
  [FEATURE_FLAG_NAMES.TEST_GLOBAL_FLAG]: {
    isEnabledGlobally: true,
    enabledUserGroups: [],
    enabledUserEmails: []
  },
  [FEATURE_FLAG_NAMES.TEST_GROUP_FLAG]: {
    isEnabledGlobally: false,
    enabledUserGroups: ['test-group-id-1', 'test-group-id-2'],
    enabledUserEmails: []
  },
  [FEATURE_FLAG_NAMES.TEST_EMAIL_FLAG]: {
    isEnabledGlobally: false,
    enabledUserGroups: [],
    enabledUserEmails: ['ismoil@datox.ai']
  },
  // Off, deliberately. This table is the fallback for flags the backend has not heard of, and a
  // fallback of `isEnabledGlobally: true` — which is what this was — makes a rollout switch that
  // only turns off: every company missing from the features map got the redesign. Absent now means
  // off, which is what per-client rollout means.
  //
  // The backend sends `v2_ui` now, so this entry is only what happens if it ever stops: off. The
  // stopgap that stood here — everyone at Datox by email domain — is gone, which is the point of
  // a per-company flag: one place decides, and it is not this file.
  [FEATURE_FLAG_NAMES.V2_UI]: {
    isEnabledGlobally: false,
    enabledUserGroups: [],
    enabledUserEmails: []
  }
};

/**
 * Hook to check if a feature flag is enabled.
 *
 * Reads features from the GET /api/user-groups/user response (React Query cache).
 * Falls back to legacy FEATURE_TOGGLES_DATA for hardcoded flags.
 */
export const useFeatureFlag = (flagName: string): boolean => {
  const { data } = useUserGroupsCurrentUserAdminState() as { data: any };
  const features: Record<string, boolean> = data?.features || {};
  if (flagName in features) {
    return features[flagName] === true;
  }

  // Legacy hardcoded fallback
  const featureToggle = FEATURE_TOGGLES_DATA[flagName];
  if (!featureToggle) return false;
  if (featureToggle.isEnabledGlobally) return true;

  const store = authStore as AuthStore;
  const userEmail = (store?.user?.username as string | undefined)?.toLowerCase();
  if (!userEmail) return false;
  return !!featureToggle.enabledUserEmails?.some(email => email.toLowerCase() === userEmail);
};

/**
 * Whether the answer above is an answer yet.
 *
 * `useFeatureFlag` says false while GET /api/user-groups/user is in flight, which is right for
 * hiding a button and wrong for choosing between two designs of the same page: the reader watches
 * the classic one draw and then be replaced. Pages that swap themselves wait for this.
 */
export const useFeatureFlagsReady = (): boolean => {
  const { isLoading, isFetched } = useUserGroupsCurrentUserAdminState() as any;
  return !isLoading && !!isFetched;
};

/**
 * Returns the full features map for the current user's company.
 * Source: GET /api/user-groups/user → response.features
 */
export const useCompanyFeatures = (): Record<string, boolean> => {
  const { data } = useUserGroupsCurrentUserAdminState() as { data: any };
  return (data?.features as Record<string, boolean>) || {};
};
